import { useRef, useState } from 'react'
import { VisualScreen } from 'app/frontend/types/custom/VisualPlan'

const COALESCE_MS = 400
const MAX_HISTORY = 100

type ScreenHistory = {
  screens: VisualScreen[]
  commit: (next: VisualScreen[]) => void
  reset: (next: VisualScreen[]) => void
  undo: () => void
  redo: () => void
  canUndo: boolean
  canRedo: boolean
}

const useScreenHistory = (): ScreenHistory => {
  const [screens, setScreens] = useState<VisualScreen[]>([])
  const pastRef = useRef<VisualScreen[][]>([])
  const futureRef = useRef<VisualScreen[][]>([])
  const lastCommitAtRef = useRef<number>(0)
  const currentRef = useRef<VisualScreen[]>([])
  const [canUndo, setCanUndo] = useState(false)
  const [canRedo, setCanRedo] = useState(false)

  const sync = (next: VisualScreen[]) => {
    currentRef.current = next
    setScreens(next)
    setCanUndo(pastRef.current.length > 0)
    setCanRedo(futureRef.current.length > 0)
  }

  const commit = (next: VisualScreen[]) => {
    const now = Date.now()
    const coalescing = now - lastCommitAtRef.current < COALESCE_MS
    lastCommitAtRef.current = now
    futureRef.current = []
    if (coalescing && pastRef.current.length > 0) {
      sync(next)
      return
    }
    const snapshot = currentRef.current
    pastRef.current = [...pastRef.current, snapshot].slice(-MAX_HISTORY)
    sync(next)
  }

  const reset = (next: VisualScreen[]) => {
    pastRef.current = []
    futureRef.current = []
    lastCommitAtRef.current = 0
    sync(next)
  }

  const undo = () => {
    const past = pastRef.current
    if (past.length === 0) return
    const previous = past[past.length - 1]
    pastRef.current = past.slice(0, -1)
    futureRef.current = [currentRef.current, ...futureRef.current]
    lastCommitAtRef.current = 0
    sync(previous)
  }

  const redo = () => {
    const future = futureRef.current
    if (future.length === 0) return
    const next = future[0]
    futureRef.current = future.slice(1)
    pastRef.current = [...pastRef.current, currentRef.current]
    lastCommitAtRef.current = 0
    sync(next)
  }

  return { screens, commit, reset, undo, redo, canUndo, canRedo }
}

export default useScreenHistory
